// Pure helpers for the React Native shadow generator. Produces the RN style
// object (iOS shadow* props + Android elevation) and a web-friendly boxShadow
// string used only for the live preview. Browser-local, no dependencies.

export interface RnShadowState {
  shadowColor: string;
  offsetX: number;
  offsetY: number;
  opacity: number;
  radius: number;
  elevation: number;
}

export const DEFAULT_RN_SHADOW_STATE: RnShadowState = {
  shadowColor: '#1f2937',
  offsetX: 0,
  offsetY: 4,
  opacity: 0.3,
  radius: 8,
  elevation: 6,
};

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  let clean = hex.replace('#', '').trim();
  if (clean.length === 3) {
    clean = clean
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) {
    return { r: 0, g: 0, b: 0 };
  }
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
}

export function rnShadowRgba(state: RnShadowState): string {
  const { r, g, b } = hexToRgb(state.shadowColor);
  const alpha = Math.round(Math.max(0, Math.min(1, state.opacity)) * 100) / 100;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function rnShadowPreviewCss(state: RnShadowState): string {
  return `${state.offsetX}px ${state.offsetY}px ${state.radius}px ${rnShadowRgba(state)}`;
}

export function rnShadowStyleObject(state: RnShadowState): string {
  return `const styles = StyleSheet.create({
  shadow: {
    shadowColor: '${state.shadowColor}',
    shadowOffset: { width: ${state.offsetX}, height: ${state.offsetY} },
    shadowOpacity: ${state.opacity},
    shadowRadius: ${state.radius},
    elevation: ${state.elevation},
  },
});`;
}
